<template>
  <div class="flight-check-in-status">
    <div class="flight-check-in-status__row">
      <div
        :class="[
          'flight-check-in-status__status',
          `flight-check-in-status__status--${status_info.status}`,
        ]"
      >
        <vb-icon
          :name="status_info.icon"
          :size="16"
          :color="sass(status_info.color)"
        />
        <span>
          {{ status_info.display }}
        </span>
        <span v-if="!is_homogenous_state && itinerary_label">
          ({{ itinerary_label }})
        </span>
      </div>
    </div>
    <div class="flight-check-in-status__row" v-html="status_info.description()">
    </div>
    <div
      v-for="group in boarding_pass_groups"
      :key="group.key"
      class="flight-check-in-status__row"
    >
      <div
        v-if="group.label && boarding_pass_groups.length > 1"
        class="flight-check-in-status__itinerary"
      >
        {{ group.label }}
      </div>
      <div class="flight-check-in-status__boarding-passes">
        <boarding-pass-link
          v-for="(traveler, index) in group.travelers"
          :key="traveler.key"
          :boarding_passes="traveler"
          :index="index"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import BoardingPassLink from '@liveflight/components/blocks/check-in/boarding-pass-link.vue';

  // first_name/last_name arrive on the grid cell and on the flat pass alike.
  const traveler_label = (pass, index) => {
    const { first_name = '', last_name = '', passenger_id } = pass;
    return (
      [first_name, last_name].filter(Boolean).join(' ') ||
      `Boarding Pass ${passenger_id || index + 1}`
    );
  };

  const PENDING_STATUS = {
    icon: 'hourglass',
    status: 'pending',
    color: 'primary',
    display: 'Il tuo check-in online è programmato.',
    description: () =>
      "Riceverai un'email per inserire i dati/documenti necessari (se non già forniti). Effettueremo il check-in non appena la compagnia aerea aprirà le registrazioni (di solito 24-48h prima della partenza) e ti invieremo le carte d'imbarco via email.",
  };

  const POSSIBLE_STATUS_INFO = Object.fromEntries(
    [
      PENDING_STATUS,
      {
        icon: 'cross_circle',
        status: 'not_requested',
        color: 'base',
        display: 'Servizio non richiesto',
        description: () =>
          'Non hai richiesto il servizio di check-in automatico per questa prenotazione.',
      },
      {
        icon: 'check_circle',
        status: 'success',
        color: 'success',
        display: 'Check-in completato con successo!',
        description: () =>
          'Le tue carte d’imbarco sono pronte e sono state inviate al tuo indirizzo email.',
      },
      ...['failed', 'canceled'].map((status) => ({
        icon: 'cross_circle',
        color: 'danger',
        status,
        display: 'Richiesta fallita',
        description: () =>
          "Non è stato possibile completare il check-in automatico (ad es. per verifica documenti richiesta dalla compagnia in aeroporto). Ti invitiamo a procedere manualmente sul sito della compagnia con il PNR indicato sopra.",
      })),
    ].map((element) => [element.status, element])
  );

  export default {
    name: 'flight-check-in-status',
    components: {
      'boarding-pass-link': BoardingPassLink,
    },
    props: {
      flight: { type: Object, required: true },
      is_homogenous_state: { type: Boolean, default: false },
    },
    computed: {
      itinerary_label() {
        const { origin = '', destination = '' } = this.flight;
        if (!origin || !destination) {
          return '';
        }
        return `${origin} - ${destination}`;
      },
      status_info() {
        const key = String(this.flight.status).toLowerCase().trim();
        return POSSIBLE_STATUS_INFO[key] || PENDING_STATUS;
      },
      // Every document hangs off the segment cell it belongs to, so the legs,
      // the travelers and their formats all come straight from the payload's
      // own nesting. download_url is the coming backend fallback for public_url.
      boarding_pass_groups() {
        const { segments = [] } = this.flight;
        return segments
          .map((segment) => ({
            key: `leg:${segment.leg}`,
            label: `${segment.departure_airport} - ${segment.arrival_airport}`,
            travelers: (segment.passengers || [])
              .map((cell, index) => ({
                key: `bp:${cell.passenger_journey_id}`,
                traveler_label: traveler_label(cell, index),
                formats: (cell.boarding_passes || []).filter(
                  (pass) => pass.public_url || pass.download_url
                ),
              }))
              .filter((traveler) => traveler.formats.length),
          }))
          .filter((group) => group.travelers.length);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .flight-check-in-status {
    line-height: normal;
    display: flex;
    flex-direction: column;
    gap: $grid-gutter-width / 3;
    font-size: 14px;

    &__status {
      display: flex;
      gap: 7.5px;
      align-items: center;

      font-size: 16px;
      font-weight: $vb-font-weight-semibold;

      &--success {
        color: $success;
      }
      &--canceled,
      &--failed {
        color: $danger;
      }
      &--pending {
        color: var(--primary);
      }
    }

    &__boarding-passes {
      margin-top: $grid-gutter-width / 6;
      display: flex;
      flex-wrap: wrap;
      // A badge is far taller than a text link: keep the two on one midline.
      align-items: center;
      row-gap: $grid-gutter-width / 2;
      column-gap: $grid-gutter-width;
    }

    &__itinerary {
      margin-bottom:  $grid-gutter-width / 2;
      font-size: 16px;
      font-weight: $vb-font-weight-semibold;
    }
  }
</style>
